import React, { useEffect, useMemo, useState } from 'react';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import { AddItemModal } from 'components/Shared/InputOutputModal/AddItemModal';
import { SelectWithAdd } from 'components/Shared/SelectWithAdd';
import { useGetOrganizationsIdentityQuery } from 'redux/apiSlice/organizations.slice';
import {
  useGetPeopleIdentityQuery,
  useGetPeopleByIdQuery,
} from 'redux/apiSlice/people.slice';
import CreateOrganizationModal from 'components/Shared/CreateOrganizationModal';
import { TCreateOrganizationModalOpen } from 'components/Shared/CreateOrganizationModal/CreateOrganizationModal.types';
import CreateCoordinatorModal from 'components/Shared/Coordinator/CreateCoordinatorModal';
import { TCreateCoordinatorModalOpen } from 'components/Shared/Coordinator/CreateCoordinatorModal/CreateCoordinatorModal.types';
import { useGetElcIdentitiesQuery } from 'redux/apiSlice/elcs.slice';
import { EntitySelectorProps, EntityOption } from './EntitySelector.types';

export const EntitySelector: React.FC<EntitySelectorProps> = ({
  value,
  onChange,
  placeholder = 'Select entity',
  entityType = 'ORGANIZATION',
  disabled = false,
  'data-cy': dataCy,
}) => {
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const { showToastNotification } = useShowToastNotification();
  const [localCreatedOptions, setLocalCreatedOptions] = useState<
    EntityOption[]
  >([]);
  const [createOrgOpen, setCreateOrgOpen] =
    useState<TCreateOrganizationModalOpen>({
      visible: false,
      initialOrgName: '',
    });

  const [createPersonOpen, setCreatePersonOpen] =
    useState<TCreateCoordinatorModalOpen>({
      visible: false,
      initialName: '',
    });
  const [createdPersonId, setCreatedPersonId] = useState<string>('');

  const handleOrganizationCreated = (org: { value: string; label: string }) => {
    onChange?.(org.value);
    setLocalCreatedOptions(prev => {
      if (prev.some(opt => opt.value === org.value)) return prev;
      return [{ label: org.label, value: org.value }, ...prev];
    });
  };

  const handlePersonCreated = (personId: string) => {
    setCreatedPersonId(personId);
    onChange?.(personId);
  };

  // Data sources by entity type
  const { data: orgEntities = [], isFetching: isFetchingOrgs } =
    useGetOrganizationsIdentityQuery(undefined, {
      skip: entityType !== 'ORGANIZATION',
    });
  const { data: peopleIdentity = [], isFetching: isFetchingPeople } =
    useGetPeopleIdentityQuery(undefined, {
      skip: entityType !== 'PEOPLE',
    });

  const { data: elcIdentity = [], isFetching: isFetchingElc } =
    useGetElcIdentitiesQuery(undefined, {
      skip: entityType !== 'ELC',
    });

  const { data: createdPersonDetails } = useGetPeopleByIdQuery(
    { userId: createdPersonId },
    { skip: !createdPersonId },
  );

  const entities: EntityOption[] = useMemo(() => {
    if (!entityType) return [];
    switch (entityType) {
      case 'ORGANIZATION':
        return (orgEntities as EntityOption[]) || [];
      case 'PEOPLE':
        return (peopleIdentity as unknown as EntityOption[]) || [];
      case 'ELC':
        return (elcIdentity as unknown as EntityOption[]) || [];
      default:
        return [];
    }
  }, [entityType, orgEntities, peopleIdentity, elcIdentity]);

  const isFetching = isFetchingOrgs || isFetchingPeople || isFetchingElc;
  const isCreating = false;

  const mergedOptions = useMemo(() => {
    const map = new Map<string, EntityOption>();
    [...entities, ...localCreatedOptions].forEach(opt => {
      if (!map.has(opt.value)) map.set(opt.value, opt);
    });
    return Array.from(map.values());
  }, [entities, localCreatedOptions]);

  // Prevent displaying raw IDs when value doesn't match current options
  // (e.g., after switching between Organization/ELC/Person tabs)
  const effectiveValue = useMemo(() => {
    if (!value) return undefined;
    return mergedOptions.some(opt => opt.value === value) ? value : undefined;
  }, [value, mergedOptions]);

  useEffect(() => {
    if (!createdPersonId || !createdPersonDetails?.fullName) return;
    const existsInMerged = mergedOptions.some(
      opt => opt.value === createdPersonId,
    );
    if (!existsInMerged) {
      setLocalCreatedOptions(prev => {
        if (prev.some(opt => opt.value === createdPersonId)) return prev;
        return [
          { label: createdPersonDetails.fullName, value: createdPersonId },
          ...prev,
        ];
      });
    }
  }, [createdPersonId, createdPersonDetails, mergedOptions]);

  useEffect(() => {
    if (!entities?.length) return;
    setLocalCreatedOptions(prev =>
      prev.filter(loc => !entities.some(s => s.value === loc.value)),
    );
  }, [entities]);

  useEffect(() => {
    setLocalCreatedOptions([]);
  }, [entityType]);

  const handleAddEntity = () => {
    if (entityType === 'ORGANIZATION') {
      setCreateOrgOpen({ visible: true, initialOrgName: '' });
      return;
    }
    if (entityType === 'PEOPLE') {
      setCreatePersonOpen({ visible: true, initialName: '' });
      return;
    }
    setIsAddModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsAddModalVisible(false);
  };

  const handleEntityAdd = async (entityName: string) => {
    try {
      if (!entityType) return;

      // TODO: Replace with actual API call
      // const newEntity: EntityOption = await createEntity({
      //   type: entityType,
      //   name: entityName,
      // }).unwrap();

      // Temporary mock creation - replace with actual API call
      const newEntity: EntityOption = {
        label: entityName,
        value: `${entityType.toLowerCase()}-${Date.now()}`,
      };

      setLocalCreatedOptions(prev => [newEntity, ...prev]);

      onChange?.(newEntity.value);
      setIsAddModalVisible(false);
      showToastNotification({
        message: `${
          entityType.charAt(0) + entityType.slice(1).toLowerCase()
        } created`,
        type: ENotificationType.SUCCESS,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to add entity:', error);
      showToastNotification({ type: ENotificationType.ERROR });
    }
  };

  const handleSelectionChange = (selectedValue: string) => {
    // Pass the selection to the parent component
    onChange?.(selectedValue);
  };

  const getModalTitle = () => {
    switch (entityType) {
      case 'ORGANIZATION':
        return 'Add Organization';
      case 'PEOPLE':
        return 'Add Person';
      case 'ELC':
        return 'Add ELC';
      default:
        return 'Add Entity';
    }
  };

  const getFieldLabel = () => {
    switch (entityType) {
      case 'ORGANIZATION':
        return 'Organization Name';
      case 'PEOPLE':
        return 'Person Name';
      case 'ELC':
        return 'ELC Name';
      default:
        return 'Entity Name';
    }
  };

  const getAddButtonLabel = () => {
    switch (entityType) {
      case 'ORGANIZATION':
        return 'Add Organization';
      case 'PEOPLE':
        return 'Add Person';
      case 'ELC':
        return 'Add ELC';
      default:
        return 'Add Entity';
    }
  };

  const getEmptyLabel = () => {
    switch (entityType) {
      case 'ORGANIZATION':
        return 'No organizations';
      case 'PEOPLE':
        return 'No people';
      case 'ELC':
        return 'No ELC centers';
      default:
        return 'No entities';
    }
  };

  return (
    <>
      <SelectWithAdd
        options={mergedOptions}
        value={effectiveValue}
        onChange={handleSelectionChange}
        placeholder={placeholder}
        onAdd={
          entityType === 'ORGANIZATION' || entityType === 'PEOPLE'
            ? handleAddEntity
            : undefined
        }
        addButtonLabel={getAddButtonLabel()}
        emptyOptionsLabel={getEmptyLabel()}
        disabled={disabled || isFetching}
        data-cy={dataCy}
      />

      {entityType === 'ORGANIZATION' && (
        <CreateOrganizationModal
          open={createOrgOpen}
          setOpen={setCreateOrgOpen}
          setSelectedOrganization={handleOrganizationCreated}
        />
      )}

      {entityType === 'PEOPLE' && (
        <CreateCoordinatorModal
          open={createPersonOpen}
          setOpen={setCreatePersonOpen}
          setCoordinatorId={handlePersonCreated}
          label="Person"
        />
      )}

      <AddItemModal
        visible={isAddModalVisible}
        onCancel={handleModalCancel}
        onAdd={handleEntityAdd}
        title={getModalTitle()}
        fieldLabel={getFieldLabel()}
        placeholder="Enter Name"
        loading={isCreating}
        data-cy={`${dataCy}-add-modal`}
      />
    </>
  );
};
